/**
 * useHeaderLogic - State and actions for the global Header (nav, profile, modals, notifications).
 * Returns { state, actions }.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/context/AuthContext';
import AuthService from '@/core/auth/AuthService';
import UserPreferencesService, {
  isIncompleteLegalSubjectPreferences,
} from '@/shared/services/UserPreferencesService';
import NotificationService from '@/shared/services/NotificationService';
import { scheduleNotificationCountRefresh } from '@/shared/utils/notificationCountSync';
import { isAdminRoleName, normalizePlatformRoles } from '@/shared/auth/obligationAuth';

export const ROLE_LABELS = {
  CITIZEN: 'Грађанин',
  ADMIN: 'Администратор',
  EVALUATOR: 'Евалуатор',
  OGP_ADMIN: 'ОГП Администратор',
  MODERATOR: 'Модератор',
  TEAM_LEAD: 'Руководилац',
};

const getSupportedRoles = (user) => normalizePlatformRoles(user);

export function getRoleLabel(role) {
  return ROLE_LABELS[role] || role;
}

export function useHeaderLogic() {
  const { user, login, logout } = useAuth();
  const { pathname } = useLocation();
  const isUserLoggedIn = !!user;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedOrganizationName, setSelectedOrganizationName] = useState(null);
  const [selectedInstitutionName, setSelectedInstitutionName] = useState(null);
  const [activeRole, setActiveRole] = useState(null);
  const [userType, setUserType] = useState('individual');
  const [prefsIncomplete, setPrefsIncomplete] = useState(false);

  const loadUnreadCount = useCallback(async ({ forceRefresh = false } = {}) => {
    // Zahteva loginData — bez njega BE /login još nije potvrdio sesiju i notification/search bi
    // dobio 401 što bi pokrenulo renewal → iframe → X-Frame-Options grešku.
    if (!isUserLoggedIn || !user?.loginData) return;
    try {
      const data = await NotificationService.search(false, { forceRefresh });
      setUnreadCount((data || []).length);
    } catch {
      setUnreadCount(0);
    }
  }, [isUserLoggedIn, user]);

  const syncUnreadCount = useCallback((count) => {
    setUnreadCount(typeof count === 'number' && count >= 0 ? count : 0);
  }, []);

  const userFullName =
    user?.loginData?.profile?.first_name && user?.loginData?.profile?.last_name
      ? `${user.loginData.profile.first_name} ${user.loginData.profile.last_name}`.trim()
      : user?.profile
        ? `${user.profile['http://schema.id.rs/claims/givenname'] || ''} ${user.profile['http://schema.id.rs/claims/familyname'] || ''}`.trim()
        : '';

  const displayName =
    isAdminRoleName(activeRole)
      ? 'ADMINISTRATOR'
      : prefsIncomplete
        ? (userFullName || 'Профил')
        : userType === 'organization'
          ? (selectedOrganizationName || 'Организација')
          : userType === 'institution'
            ? (selectedInstitutionName || 'Институција')
            : (userFullName || 'Профил');

  useEffect(() => {
    if (isUserLoggedIn) {
      const preferences = UserPreferencesService.getPreferences();
      setSelectedOrganizationName(preferences.selectedOrganizationName || null);
      setSelectedInstitutionName(preferences.selectedInstitutionName || null);
      setUserType(preferences.userType || 'individual');
      setPrefsIncomplete(isIncompleteLegalSubjectPreferences(preferences));
      const roles = getSupportedRoles(user);
      if (roles.length > 0) {
        const preferredRole = preferences.selectedRole;
        setActiveRole(preferredRole && roles.includes(preferredRole) ? preferredRole : roles[0]);
      }
    } else {
      setSelectedOrganizationName(null);
      setSelectedInstitutionName(null);
      setActiveRole(null);
      setUserType('individual');
      setPrefsIncomplete(false);
    }
  }, [isUserLoggedIn, user]);

  useEffect(() => {
    if (isUserLoggedIn) {
      loadUnreadCount({ forceRefresh: true });
    } else {
      setUnreadCount(0);
    }
  }, [isUserLoggedIn, loadUnreadCount]);

  // SPA navigacija — osveži unread (i profilski query preko notificationsUpdated).
  useEffect(() => {
    if (!isUserLoggedIn) return undefined;
    scheduleNotificationCountRefresh();
    return undefined;
  }, [pathname, isUserLoggedIn]);

  useEffect(() => {
    const onFocus = () => {
      if (isUserLoggedIn) loadUnreadCount({ forceRefresh: true });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isUserLoggedIn, loadUnreadCount]);

  useEffect(() => {
    const onNotificationsUpdated = () => {
      if (isUserLoggedIn) loadUnreadCount({ forceRefresh: true });
    };
    window.addEventListener('notificationsUpdated', onNotificationsUpdated);
    return () => window.removeEventListener('notificationsUpdated', onNotificationsUpdated);
  }, [isUserLoggedIn, loadUnreadCount]);

  useEffect(() => {
    const syncFromPreferences = () => {
      if (!isUserLoggedIn) return;
      const preferences = UserPreferencesService.getPreferences();
      setSelectedOrganizationName(preferences.selectedOrganizationName || null);
      setSelectedInstitutionName(preferences.selectedInstitutionName || null);
      setUserType(preferences.userType || 'individual');
      setPrefsIncomplete(isIncompleteLegalSubjectPreferences(preferences));
      const roles = getSupportedRoles(user);
      if (roles.length > 0 && preferences.selectedRole && roles.includes(preferences.selectedRole)) {
        setActiveRole(preferences.selectedRole);
      }
    };
    window.addEventListener('storage', syncFromPreferences);
    window.addEventListener('focus', syncFromPreferences);
    window.addEventListener('preferencesChanged', syncFromPreferences);
    return () => {
      window.removeEventListener('storage', syncFromPreferences);
      window.removeEventListener('focus', syncFromPreferences);
      window.removeEventListener('preferencesChanged', syncFromPreferences);
    };
  }, [isUserLoggedIn, user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const mojProfil = document.getElementById('mojProfil');
      const profilePopup = document.getElementById('mojProfilUl');
      if (
        mojProfil &&
        profilePopup &&
        !mojProfil.contains(event.target) &&
        !profilePopup.contains(event.target)
      ) {
        setIsProfileOpen(false);
      }
    };
    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isProfileOpen]);

  const closeBurger = useCallback(() => {
    setIsMenuOpen(false);
    const header = document.querySelector('header');
    if (header) header.classList.remove('open');
  }, []);

  const handleLoginClick = useCallback(() => {
    if (login) {
      login();
    } else {
      AuthService.login();
    }
    closeBurger();
  }, [login, closeBurger]);

  const handleLogoutClick = useCallback(() => {
    if (logout) {
      logout();
    } else {
      AuthService.logout();
    }
    setIsProfileOpen(false);
    closeBurger();
    UserPreferencesService.clearPreferences();
  }, [logout, closeBurger]);

  const handleSettingsClick = useCallback((e) => {
    e.preventDefault();
    setShowSettingsModal(true);
    setIsProfileOpen(false);
  }, []);

  const handleProfileBlur = useCallback((e) => {
    const clickedElement = e.relatedTarget;
    const mojProfil = document.getElementById('mojProfil');
    if (
      mojProfil &&
      (!clickedElement || !clickedElement.classList.contains('my-egov-link'))
    ) {
      setIsProfileOpen(false);
    }
  }, []);

  const toggleProfileOpen = useCallback(() => {
    setIsProfileOpen((prev) => !prev);
  }, []);

  const toggleBurger = useCallback(() => {
    setIsMenuOpen((prev) => {
      const next = !prev;
      const header = document.querySelector('header');
      if (header) {
        if (next) header.classList.add('open');
        else header.classList.remove('open');
      }
      return next;
    });
  }, []);

  const setActiveRoleAndPersist = useCallback((role) => {
    setActiveRole(role);
    const preferences = UserPreferencesService.getPreferences();
    preferences.selectedRole = role;
    UserPreferencesService.savePreferences(preferences);
    window.dispatchEvent(new Event('preferencesChanged'));
  }, []);

  const closeProfileAndNavigate = useCallback(() => {
    setIsProfileOpen(false);
  }, []);

  const closeProfileOnMenuClick = useCallback(() => {
    setIsProfileOpen(false);
  }, []);

  return {
    state: {
      user,
      isUserLoggedIn,
      isMenuOpen,
      isProfileOpen,
      showSettingsModal,
      unreadCount,
      selectedOrganizationName,
      selectedInstitutionName,
      activeRole,
      userType,
      displayName,
      userFullName,
    },
    actions: {
      setShowSettingsModal,
      handleLoginClick,
      handleLogoutClick,
      handleSettingsClick,
      handleProfileBlur,
      toggleProfileOpen,
      closeBurger,
      toggleBurger,
      setActiveRoleAndPersist,
      closeProfileAndNavigate,
      closeProfileOnMenuClick,
      loadUnreadCount,
      syncUnreadCount,
    },
  };
}
